import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../../../../foundation/altzero_base/base/lib/utils';
import type { MismatchDrivers } from '../../lib/mismatchDrivers';

// Why bills mismatch (lib/mismatchDrivers): by section, by retailer, and the
// same difference recurring across bills. A retailer row filters the table by
// retailer; a recurring row filters it to that difference's bills.

type MismatchDriversPanelProps = {
  drivers: MismatchDrivers;
  /** The recurring difference filtering the table, if any. */
  activeDriver: string | null;
  activeRetailers: readonly string[];
  onDriver: (key: string | null) => void;
  onRetailer: (retailer: string) => void;
};

const fmtMoney = (n: number) => n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
const th = 'px-2 py-1 text-left font-medium text-gray-500';
const td = 'px-2 py-1 text-gray-700';

export function MismatchDriversPanel({ drivers, activeDriver, activeRetailers, onDriver, onRetailer }: MismatchDriversPanelProps) {
  const [open, setOpen] = useState(true);
  const { sections, retailers, recurring } = drivers;
  if (sections.length === 0 && retailers.length === 0) return null;

  return (
    <div data-testid="bill-runs2-drivers" className="rounded-lg border border-gray-200 bg-white">
      <button type="button" onClick={() => setOpen((x) => !x)}
        className="flex w-full items-center gap-1 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-700">
        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} Mismatch drivers
        {activeDriver && <span className="ml-2 normal-case tracking-normal font-normal text-blue-700">filtering</span>}
      </button>
      {open && (
        <div className="grid gap-4 border-t px-3 py-3 text-xs lg:grid-cols-3">
          <div>
            <p className="mb-1 font-medium text-gray-800">Recurring differences</p>
            {recurring.length === 0 ? (
              <p className="text-gray-400">None — no difference repeats across bills.</p>
            ) : (
              <table className="w-full">
                <thead><tr><th className={th}>Section · Retailer</th><th className={cn(th, 'text-right')}>Amount</th><th className={cn(th, 'text-right')}>Bills</th></tr></thead>
                <tbody>
                  {recurring.map((r) => (
                    <tr key={r.key} data-testid="bill-runs2-driver-recurring"
                      onClick={() => onDriver(activeDriver === r.key ? null : r.key)}
                      className={cn('cursor-pointer hover:bg-gray-50', activeDriver === r.key && 'bg-blue-50')}>
                      <td className={td}>{r.label} · {r.retailer || '—'}</td>
                      <td className={cn(td, 'text-right tabular-nums', r.amount > 0 ? 'text-red-600' : 'text-orange-600')}>{fmtMoney(r.amount)}</td>
                      <td className={cn(td, 'text-right tabular-nums')}>{r.billIds.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <p className="mb-1 font-medium text-gray-800">By section</p>
            <table className="w-full">
              <thead><tr><th className={th}>Section</th><th className={cn(th, 'text-right')}>Bills</th><th className={cn(th, 'text-right')}>Over</th><th className={cn(th, 'text-right')}>Under</th></tr></thead>
              <tbody>
                {sections.map((s) => (
                  <tr key={s.section}>
                    <td className={td}>{s.label}</td>
                    <td className={cn(td, 'text-right tabular-nums')}>{s.bills}</td>
                    <td className={cn(td, 'text-right tabular-nums text-red-600')}>{s.over}</td>
                    <td className={cn(td, 'text-right tabular-nums text-orange-600')}>{s.under}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div>
            <p className="mb-1 font-medium text-gray-800">By retailer</p>
            <table className="w-full">
              <thead><tr><th className={th}>Retailer</th><th className={cn(th, 'text-right')}>Mismatched</th><th className={cn(th, 'text-right')}>Rate</th><th className={cn(th, 'text-right')}>Over</th><th className={cn(th, 'text-right')}>Under</th></tr></thead>
              <tbody>
                {retailers.map((r) => (
                  <tr key={r.retailer} data-testid="bill-runs2-driver-retailer" onClick={() => r.retailer && onRetailer(r.retailer)}
                    className={cn('cursor-pointer hover:bg-gray-50', activeRetailers.includes(r.retailer) && 'bg-blue-50')}>
                    <td className={td}>{r.retailer || '—'}</td>
                    <td className={cn(td, 'text-right tabular-nums')}>{r.mismatched} / {r.bills}</td>
                    <td className={cn(td, 'text-right tabular-nums')}>{r.rate}%</td>
                    <td className={cn(td, 'text-right tabular-nums text-red-600')}>{r.over}</td>
                    <td className={cn(td, 'text-right tabular-nums text-orange-600')}>{r.under}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
